import React, {useEffect, useState} from "react";
import {GridCell, gridConstants, GridContainer} from "../../../Components/Grid";
import {userApprovalApi} from "./userApprovalApi";
import {Button, ButtonLinkStyle} from "../../../Components/Inputs";
import {PageTitle} from "../../../Components/Landmarks";
import {UserApprovalView} from "./UserApprovalView";
import {convertToNumber, isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {userModel} from "../userFactory";
import {UserApprovalSearchFormView} from "./UserApprovalSearchFormView";
import {Allow, allow, policyEvents} from "../../../Components/Authorize";
import {APPROVAL_STATUS, getApprovalStatusName} from "../approvalConstants";
import {apiConstants} from "../../../Utilities/Api";
import {approveUserModel, userApprovalUpdateResultsModel} from "./userApprovalFactory";
import {locations} from "../../../Utilities/Location";
import {isArrayNullOrEmpty} from "../../../Utilities/Types/arrayUtilities";
import {LayoutFullWidth} from "../../../Components/Layout";
import {SearchResultsContainer} from "../../../Components/SearchResults";

import type { ListItem } from "../../../Utilities/Types/listUtilities";
import type { SearchCriteria } from "../../../types";

interface UserApprovalContainerProps {
districts: Record<string, any>[];
handleApiCall: (...args: unknown[]) => void;
handleSetUserSearchCriteria: (criteria: Record<string, unknown>) => void;
isLoading: boolean;
redirect: (path: string) => void;
setNotificationBatchApiResults: (results: Record<string, unknown>, successMessage: string, partialSuccessMessage: string) => void;
userSearchCriteria: Record<string, any>;
    user: Record<string, unknown>;
}

export const UserApprovalContainer: React.FC<UserApprovalContainerProps> = ({
                                          districts,
                                          handleApiCall,
                                          handleSetUserSearchCriteria,
                                          isLoading,
                                          redirect,
                                          setNotificationBatchApiResults,
                                          userSearchCriteria,
                                          user
                                      }) => {

    const [users, setUsers] = useState<Record<string, unknown>[]>([]);
    const [initialUsers, setInitialUsers] = useState<Record<string, unknown>[]>([]);


    const onApprovalStatusClick = (userId: string, value: string | number) => {
        handleSetUserSearchCriteria({...userSearchCriteria, sortByColumn: ``, loadSearchResults: false});
        setUsers(users.map(user =>
            user.userId === userId
                ? {
                    ...user,
                    approvalStatus: convertToNumber(value),
                    approvalStatusName: getApprovalStatusName(convertToNumber(value)!)
                }
                : user
        ));
    };

    const handleApproveAllClick = () => {

        if (userSearchCriteria)
            setUsers(users.map(user => {
                const userToApprove = users.find(u => u.userId === user.userId);
                return userToApprove ? {
                    ...user,
                    approvalStatus: APPROVAL_STATUS.Approved,
                    approvalStatusName: getApprovalStatusName(APPROVAL_STATUS.Approved)
                } : user;
            }));
    };
    
      const handleExportUsersClick = () => {
            if (noResults) {
                alert("Search criteria has no results. Adjust your search and try again.")
            } else {
                handleApiCall(() => userApprovalApi.exportUsersReport(userSearchCriteria));
            }
        };

    const handleSaveClick = () => {

        const usersToUpdate = users.filter(user => {
            const initialUser = initialUsers.find(initial => initial.userId === user.userId);
            return initialUser && initialUser.approvalStatus !== user.approvalStatus;
        }).map(approveUserModel);

        if (usersToUpdate.length > 0) {
            handleApiCall(() => userApprovalApi.approveUsers(usersToUpdate), (result: Record<string, unknown>) => loadUserApprovalUpdateResults(result, usersToUpdate));
        }
    };

    const loadUserApprovalUpdateResults = (result: Record<string, unknown>, updatedUsers: Record<string, unknown>[]) => {
        const userApprovalUpdateResults = userApprovalUpdateResultsModel({...result});

        const suffix = userApprovalUpdateResults.numberUpdated === 1 ? `` : `s`;
        setNotificationBatchApiResults(
            userApprovalUpdateResults,
            apiConstants.successMessage.approval.APPROVAL_UPDATED,
            `Successfully updated the approval status of ${userApprovalUpdateResults.numberUpdated} user${suffix}.`
        );

        setInitialUsers(users.map(user => {
            const updatedUser = updatedUsers.find(updated => updated.userId === user.userId);
            return updatedUser ? {...user, approvalStatus: updatedUser.approvalStatus} : user;
        }));
    };

    const loadUsersResults = (result: Record<string, unknown>[]) => {
        const pendingPatrons = result.map(userModel);
        setUsers(pendingPatrons);
        setInitialUsers(pendingPatrons);
    };

    const loadUsers = () => {
        handleApiCall(() => userApprovalApi.getUsers({
            ...userSearchCriteria,
        }), loadUsersResults);

    }

    const buttonSharedSettings = {
        medium_columns: gridConstants.column.THREE,
        small_columns: gridConstants.column.SIX,
    };


     const updateUserInState = (userId: string, notes: string) => {
        setUsers((currentUsers) =>
            currentUsers.map((user) =>
                user.userId === userId
                    ? { ...user, notes }
                    : user
            )
        );
    };

    
    const handleSaveUpdatedUserNotes = (updatedUser: Record<string, any>) => {
        updateUserInState(updatedUser.userId, updatedUser.notes);
        handleApiCall(
            () => userApprovalApi.updateUserNotes(updatedUser.userId, updatedUser.notes),
            () => loadUsers()
        );
    };

    

    const noResults = isArrayNullOrEmpty(users);

    useEffect(() => {
        if (isNullOrUndefined(districts) || districts.length === 0) return;

        if (userSearchCriteria.loadSearchResults)
            loadUsers();

    }, [districts, userSearchCriteria]);

    useEffect(() => {
        if (!allow(policyEvents.VIEW))
            redirect(locations.DASHBOARD.path);
    }, []);

    if (!userSearchCriteria) return null;

    return <>
        <PageTitle h1={`User Management`}/>
        <div className={`searchBox`}>
            <div className={`searchBox__title`}>Search for Users</div>
            <UserApprovalSearchFormView
                buttonSharedSettings={buttonSharedSettings}
                districts={districts}
                handleSetUserSearchCriteria={handleSetUserSearchCriteria}
                isLoading={isLoading}
                userSearchCriteria={userSearchCriteria}
                user={user}
            />
        </div>


        <LayoutFullWidth>
            <SearchResultsContainer>
            <div>
                <GridContainer isReverseOrder hasMarginX hasMarginY className={`text-right`}>
                    <GridCell>
                        <ButtonLinkStyle
                            isCollapsed
                            label={`Export Users`}
                            name="btnExportUsers"
                            onClick={handleExportUsersClick}
                        />
                    </GridCell>
                </GridContainer>     


            <Allow policyEvent={policyEvents.APPROVE_USERS}>
                <GridContainer isReverseOrder className={`form__header`} hasMarginX>
                    {
                        !noResults && <>
                            <GridCell {...buttonSharedSettings}>
                                <Button
                                    disabled={isLoading}
                                    label="Save"
                                    name="btnSave"
                                    onClick={handleSaveClick}
                                    isPrimary
                                />
                            </GridCell>
                            <GridCell {...buttonSharedSettings}>
                                <Button
                                    disabled={isLoading}
                                    label="Approve all"
                                    name="btnApproveAll"
                                    onClick={handleApproveAllClick}
                                />
                            </GridCell>
                        </>
                    }
                </GridContainer>
            </Allow>

            <UserApprovalView
                handleSetUserSearchCriteria={handleSetUserSearchCriteria}
                onApprovalStatusClick={onApprovalStatusClick}
                redirect={redirect}
                users={users}
                userSearchCriteria={userSearchCriteria}
                handleSaveUpdatedUserNotes={handleSaveUpdatedUserNotes}
            />
            </div>
            </SearchResultsContainer>
        </LayoutFullWidth>




    </>;
};

